// frontend/src/hooks/useEsignGateCheck.js
//
// ESIGN-UX-REDESIGN-001 — Story 1 (post-login gate hook).
//
// Replaces the booking-flow gate (BookSession.js:680-710) by relocating the
// ESIGN check to dashboard mount-time. Fires once per authenticated mount
// against `GET /api/waivers/me`; if the customer needs the gate, navigates
// to /waivers/sign-required with the original destination preserved as
// `?next=` (Marcus's tech doc Story 1 §`?next=` deep-link contract).
//
// Spec sources (locked):
//   docs/architecture/ESIGN-UX-REDESIGN-001-tech.md (Marcus)
//     §Story 1 — gate placement + cutover posture
//     §Story 1 — `?next=` deep-link contract (sanitization rules)
//     §Edge cases — revoked / dismissed / stale envelope / concurrent sessions
//     §Error Handling Strategy — 404 = ESIGN disabled (graceful no-op)
//     §Error Handling Strategy — 500 = fail closed; do NOT proceed ungated
//   docs/design/ESIGN-UX-REDESIGN-001-post-login-gate-design.md (Zuri)
//     §3 + §16 — gate trigger + AC scenarios
//
// Cutover posture (PR-A):
//   This hook is added IN ADDITION to the existing booking-flow gate at
//   BookSession.js:680-710. Both fire; the booking-flow gate becomes the
//   safety net for any session that bypassed dashboard mount (e.g., direct
//   deep-link to /book-session with stale auth). PR-B (follow-up) removes
//   the booking-flow gate AFTER this gate is smoke-validated in prod, per
//   R-020 doctrine §1 — never an ungated window.
//
// Production-safety:
//   - Pure-additive on the existing customer dashboard path.
//   - Graceful degrade: 404 from /api/waivers/me (Story 3 ENABLE_ESIGN=false)
//     is treated as "no gate"; logs a single warn-level message.
//   - Failure-closed on 500: keeps the user on the dashboard with no gate
//     redirect (rather than redirecting them away on a transient blip).
//     The booking-flow gate (still in place during PR-A) remains the safety
//     net until PR-B lands.
//   - Idempotent: only fires once per mount, gated on auth-resolution.

import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getMyWaiverStatus,
  needsEsignGate,
  WAIVER_GATE_PATH,
} from "../services/waiverApi";

/**
 * Sanitize the `?next=` deep-link query-string parameter per Marcus's tech
 * doc Story 1 sanitization rules:
 *   - Must start with `/` (no full URLs — no `next=https://evil.example/`)
 *   - Must not contain `//` (prevents protocol-relative URLs)
 *   - Must not contain `..` (prevents traversal-style strings)
 *   - If sanitization fails -> fall back to null (caller defaults to dashboard)
 *
 * Exported separately so the gate page itself can sanitize a value pulled
 * off the URL without re-implementing the rules.
 *
 * @param {string|null|undefined} value
 * @returns {string|null}
 */
export const sanitizeNextDeepLink = (value) => {
  if (!value) return null;
  if (typeof value !== "string") return null;
  if (!value.startsWith("/")) return null;
  if (value.includes("//")) return null;
  if (value.includes("..")) return null;
  // Reject backslashes (Windows-style paths or escape-injection attempts that
  // could otherwise bypass the leading-slash check on some routing libraries).
  // Defense in depth — the prior `//` + `..` + startsWith(`/`) checks already
  // cover the canonical attack surface; this is a belt-and-suspenders extra.
  if (value.includes("\\")) return null;
  return value;
};

/**
 * Read the candidate post-login destination from a URL search-string. Honors
 * three names in priority order:
 *   1. ?next=    (Marcus's tech doc lock — new contract for the redesign)
 *   2. ?redirect= (existing convention used by Login.js + ProtectedRoute.js)
 *   3. ?returnTo= (existing convention used by BookSession gate hook)
 *
 * This trio is intentional: the deployed app already uses `redirect` and
 * `returnTo` in different code paths. Adding `next` per Marcus's contract
 * lock without removing the existing names keeps the redesign pure-additive
 * on URL contracts (no broken bookmarked / emailed deep-links).
 *
 * @param {string} search — the URLSearchParams-compatible search string.
 * @returns {string|null} sanitized destination or null.
 */
export const readNextDeepLink = (search) => {
  let params;
  try {
    params = new URLSearchParams(search || "");
  } catch (e) {
    return null;
  }
  const candidate =
    params.get("next") || params.get("redirect") || params.get("returnTo");
  return sanitizeNextDeepLink(candidate);
};

/**
 * Post-login ESIGN gate hook. Mount this on the customer dashboard component
 * (and any analogous post-auth landing surfaces — see scope discussion in
 * the tech doc; for v1, customer dashboard only).
 *
 * Behavior:
 *   - When `enabled === true` (typical: the user is authenticated and is
 *     a customer), fires once per mount: GET /api/waivers/me, evaluates
 *     needsEsignGate(), and navigates to WAIVER_GATE_PATH with `?next=`
 *     preserved when the gate is needed.
 *   - When `enabled === false`, no-op. Useful for staff/admin dashboards
 *     where the customer-facing gate has no meaning.
 *   - Idempotent within a single mount via a ref-guard. React StrictMode
 *     double-invocation in dev is tolerated (the second call is short-
 *     circuited).
 *
 * @param {object} options
 * @param {boolean} options.enabled — gate the entire hook on caller-side
 *   readiness (e.g., `Boolean(token) && userRole === 'member'`).
 * @returns {void}
 */
export const useEsignGateCheck = ({ enabled }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (hasFiredRef.current) return;
    hasFiredRef.current = true;

    let cancelled = false;

    const evaluate = async () => {
      try {
        const response = await getMyWaiverStatus();
        if (cancelled) return;
        const status = response?.data || {};
        if (!needsEsignGate(status)) {
          // Already fully signed — no gate. Stay on dashboard.
          return;
        }
        // Gate needed — preserve the original destination so the success
        // state on /waivers/return can route back. Default to the current
        // pathname+search so the user lands on the same dashboard after
        // signing.
        const currentDest = `${location.pathname}${location.search || ""}`;
        const nextValue =
          readNextDeepLink(location.search) || currentDest || "/member";
        const params = new URLSearchParams();
        params.set("next", nextValue);
        // Also propagate the legacy `returnTo` so the existing WaiverGate +
        // WaiverReturn implementation (which read returnTo) keeps working
        // with no behavioral change. The legacy `?returnTo=` is the source
        // of truth for those existing components; `?next=` is the new
        // architect-locked name surfaced to other consumers (and to the
        // gate's own future readers).
        params.set("returnTo", nextValue);
        navigate(`${WAIVER_GATE_PATH}?${params.toString()}`, { replace: true });
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) {
          // ENABLE_ESIGN=false on the backend (Story 3 graceful degrade) —
          // the /api/waivers/me route is not mounted. Treat as "no gate"
          // and proceed to the dashboard. Single warn-level log so the
          // disabled state is visible in browser devtools without scaring
          // the user.
          // eslint-disable-next-line no-console
          console.warn(
            "[esign] /api/waivers/me returned 404 — assuming ESIGN disabled, skipping gate"
          );
          return;
        }
        // Any other error: fail CLOSED on the navigation side. Do NOT
        // redirect the user. The booking-flow gate (still in place during
        // PR-A) remains the safety net; once PR-B lands, the alternative
        // is to surface a retry banner — but that's a follow-up if QA
        // observes an issue with the silent fallback. Keep the warn for
        // dev visibility.
        // eslint-disable-next-line no-console
        console.warn(
          "[esign] post-login gate check failed; remaining on dashboard",
          { status }
        );
      }
    };

    evaluate();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
};

export default useEsignGateCheck;
